/**
 * Starts the hero's background video once the page has loaded, and only the
 * one that is actually on screen at this width.
 *
 * snippets/ora-hero-video.liquid writes each video with `preload="none"` and
 * no `autoplay`, so nothing downloads until this asks for it. Until then the
 * poster -- the video's own first frame -- is what shows, so the swap is a
 * still that starts moving rather than a change of picture.
 *
 * Waiting for `load` keeps the video off the network while the page's own
 * images, fonts and scripts are fetched. The timeout is a ceiling, for a page
 * whose `load` is held up by a slow third-party script.
 */

const SELECTOR = 'video[data-ora-hero-video]';
const START_CEILING_MS = 4000;

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
let started = false;

/** @param {HTMLVideoElement} video */
function isShown(video) {
  if (typeof video.checkVisibility === 'function') return video.checkVisibility();
  return video.offsetParent !== null;
}

function sync() {
  if (!started) return;

  for (const video of document.querySelectorAll(SELECTOR)) {
    if (!(video instanceof HTMLVideoElement)) continue;

    if (isShown(video) && !reduceMotion.matches) {
      if (!video.paused) continue;
      video.muted = true;
      video.preload = 'auto';
      // Refused where autoplay is not allowed (iOS Low Power Mode, data
      // saver); the poster simply stays.
      video.play().catch(() => {});
    } else if (!video.paused) {
      video.pause();
    }
  }
}

function start() {
  if (started) return;
  started = true;
  sync();
}

if (document.readyState === 'complete') {
  start();
} else {
  window.addEventListener('load', start, { once: true });
  setTimeout(start, START_CEILING_MS);
}

// The desktop and phone videos swap at Horizon's 750px breakpoint.
window.matchMedia('(min-width: 750px)').addEventListener('change', sync);
reduceMotion.addEventListener('change', sync);
// The theme editor re-renders a section in place.
document.addEventListener('shopify:section:load', sync);
